import type { ConversationListItem } from '@chatbot/shared-types';
import { useCallback, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AppShell } from '../components/AppShell.tsx';
import { CONVERSATION_TABS, RouteTabs } from '../components/RouteTabs.tsx';
import { ChatPanel } from '../components/conversations/ChatPanel.tsx';
import { ContextPanel } from '../components/conversations/ContextPanel.tsx';
import { ConversationListPanel } from '../components/conversations/ConversationListPanel.tsx';
import { useConversationActions } from '../components/conversations/useConversationActions.ts';
import { useConversationThread } from '../components/conversations/useConversationThread.ts';
import type { ConversationStateFilter } from '../components/conversations/useConversationsList.ts';
import { useConversationsList } from '../components/conversations/useConversationsList.ts';
import { useTenantContext } from '../hooks/useTenantContext.ts';

/**
 * Workspace master-detail (estilo WhatsApp Web): lista siempre visible a la
 * izquierda, hilo al centro, contexto a la derecha.
 *
 * Rutas soportadas (cableadas en App.tsx):
 *   /conversations                       → sin selección
 *   /conversations/:tenantId/:userId     → deep-link, preselecciona el hilo
 *
 * La selección vive en la URL (no en estado local): así el deep-link, el back
 * del browser y compartir el link funcionan sin lógica extra.
 */
export function ConversationsWorkspace() {
  const { tenantId: routeTenantId, userId: routeUserId } = useParams<{
    tenantId: string;
    userId: string;
  }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeTenantId, tenantIds, adminId } = useTenantContext();

  // Default 'all': la mayoría de las conversaciones son bot_active y nunca
  // escalan; arrancar en pending_human daba la impresión de panel roto.
  const stateFilter = (searchParams.get('state') as ConversationStateFilter | null) ?? 'all';
  const search = searchParams.get('search') ?? '';
  const tenantFilter =
    searchParams.get('tenantId') ?? routeTenantId ?? activeTenantId ?? tenantIds[0] ?? '';

  const selectedId = routeUserId ? decodeURIComponent(routeUserId) : null;
  const selectedTenantId = routeTenantId ?? null;

  // Defensa en profundidad: el backend (requirePermission) es la fuente de
  // verdad; si el tenant del deep-link no está en scope, volvemos al workspace.
  useEffect(() => {
    if (!routeTenantId || tenantIds.length === 0) return;
    if (!tenantIds.includes('*') && !tenantIds.includes(routeTenantId)) {
      navigate('/conversations', { replace: true });
    }
  }, [routeTenantId, tenantIds, navigate]);

  const list = useConversationsList(tenantFilter, stateFilter, search);
  const thread = useConversationThread(selectedTenantId, selectedId);
  const actions = useConversationActions(thread.conv, thread.refetch, list.refetch);

  const setFilter = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(searchParams);
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleSelect = useCallback(
    (item: ConversationListItem) => {
      const query = searchParams.toString();
      navigate(
        `/conversations/${item.tenantId}/${encodeURIComponent(item.conversationId)}${
          query ? `?${query}` : ''
        }`,
      );
    },
    [navigate, searchParams],
  );

  const handleBack = useCallback(() => {
    const query = searchParams.toString();
    navigate(`/conversations${query ? `?${query}` : ''}`);
  }, [navigate, searchParams]);

  const hasSelection = !!selectedTenantId && !!selectedId;

  return (
    <AppShell sectionTabs={<RouteTabs tabs={CONVERSATION_TABS} />}>
      <div className="flex h-full min-h-0 overflow-hidden bg-surface-base border border-edge-subtle rounded-lg shadow-sm">
        <ConversationListPanel
          items={list.items}
          loading={list.loading}
          error={list.error}
          wsStatus={list.wsStatus}
          stateFilter={stateFilter}
          search={search}
          tenantId={tenantFilter}
          tenantIds={tenantIds}
          selectedId={selectedId}
          className={`w-full min-[780px]:w-[340px] min-[780px]:shrink-0 min-[780px]:flex ${
            hasSelection ? 'hidden' : 'flex'
          }`}
          onStateFilterChange={(value) => setFilter('state', value === 'all' ? null : value)}
          onSearchChange={(value) => setFilter('search', value)}
          onTenantChange={(value) => setFilter('tenantId', value)}
          onSelect={handleSelect}
          onRetry={list.refetch}
        />

        <ChatPanel
          conv={thread.conv}
          hasSelection={hasSelection}
          error={thread.error}
          adminId={adminId}
          actions={actions}
          className={`flex-1 min-[780px]:flex ${hasSelection ? 'flex' : 'hidden'}`}
          onRetry={thread.refetch}
          onBack={handleBack}
        />

        <ContextPanel
          conv={thread.conv}
          adminId={adminId}
          actions={actions}
          className="hidden min-[1100px]:flex w-72 shrink-0"
        />
      </div>
    </AppShell>
  );
}
